import { SANITY_TOKEN, isStaging } from "@/Configs/config";
import { useApiStore } from "@/States/api";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import i18n from "@/i18n";
import { useTrans } from "@/translations";

import { ClientConfig, createClient } from "@sanity/client";
import imageUrlBuilder from "@sanity/image-url";
import { useQuery } from "@tanstack/react-query";

import { normalizeSanityResponse } from "./functions";
import * as query from "./queries";
import { SanityQueryType, SanityResponseType } from "./types";

const clientConfig: ClientConfig = {
  projectId: "knqqkm52",
  dataset: "production",
  apiVersion: "2021-10-21",
  useCdn: true,
};

// Important: Tokens are only added in staging environment to fetch drafts
if (isStaging && SANITY_TOKEN) {
  clientConfig.token = SANITY_TOKEN;
  clientConfig.ignoreBrowserTokenWarning = true;
}

/** Init sanity client */
const client = createClient(clientConfig);

/** Converts Sanity assets to url */
export const sanityGetUrlFor = imageUrlBuilder(client);

/** Request sanity data with react query function */
export async function requestSanity(input: {
  query: SanityQueryType;
  locale?: string;
}) {
  type Response = SanityResponseType<typeof input.query>;
  const { reactQueryClient } = useApiStore.getState();
  const { languages } = useCommonEntitiesStore.getState();

  const locale = input.locale ?? i18n.language;

  if (!reactQueryClient) {
    throw new Error("Could not connect to api client");
  }

  const sanityLanguage = languages?.find(
    (el) => el.code === i18n.language,
  )?.sanityCode;

  const key = `sanity_${query[input.query]}_${locale}`;

  const response = await reactQueryClient.fetchQuery({
    queryKey: [key],
    queryFn: async () => {
      const params = sanityLanguage
        ? {
            lang: sanityLanguage,
          }
        : {};
      const fetchedData = await client.fetch<Response>(
        query[input.query],
        params,
      );
      return normalizeSanityResponse<Response>(fetchedData, input.query);
    },
  });

  return response;
}

/** Request sanity data with react query hook */
export function useSanity(input: { query: SanityQueryType }) {
  type Response = SanityResponseType<typeof input.query>;
  type NormalizedResponse = SanityResponseType<typeof input.query>;
  const { t } = useTrans();

  const reactQuery = useQuery<NormalizedResponse, Error>({
    queryKey: [`sanity_${query[input.query]}`],
    queryFn: async () => {
      const fetchedData = await client.fetch<Response>(query[input.query], {
        lang: t("langIso"),
      });

      return normalizeSanityResponse<Response>(fetchedData, input.query);
    },
  });

  return reactQuery;
}
